/* eslint-disable react/no-unescaped-entities -- Italian guide copy uses apostrophes/quotes */
import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
    BookOpen,
    LayoutDashboard,
    Users,
    Archive,
    FileText,
    Wallet,
    IdCard,
    ShieldCheck,
    Mail,
    Phone,
    Sparkles,
    CheckCircle2,
} from "lucide-react";

const SECTIONS = [
    {
        id: "panoramica",
        icon: LayoutDashboard,
        title: "Panoramica e scadenze",
        body: (
            <>
                <p>
                    La <strong>Dashboard</strong> mostra la situazione generale: statistiche sui dipendenti,
                    documenti in attesa di firma, buste paga mancanti e le ultime attività.
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li><strong>Scadenze</strong>: contratti a termine, visite mediche e documenti in scadenza, con promemoria.</li>
                    <li><strong>Conformità HR</strong>: stato delle prese visione (eIDAS) e dei consensi alla consegna telematica.</li>
                    <li><strong>Registro Email</strong>: tutte le email inviate dal portale ai dipendenti.</li>
                </ul>
            </>
        ),
    },
    {
        id: "dipendenti",
        icon: Users,
        title: "Gestione dipendenti",
        body: (
            <>
                <p>
                    Dalla pagina <strong>Dipendenti</strong> accedi alla scheda completa di ogni persona:
                    anagrafica, contratto, fascicolo documentale, acconti e tesserino.
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li><strong>Credenziali portale</strong>: attiva l'accesso del dipendente e genera il link con le credenziali da inviargli.</li>
                    <li><strong>Cessazione / riattivazione</strong> con data e motivo, direttamente dalla scheda.</li>
                    <li><strong>Gruppi</strong>: organizza i dipendenti per cliente o cantiere (con logo cliente sul tesserino).</li>
                    <li><strong>Richieste modifiche dati</strong>: approva o rifiuta le variazioni di contatto richieste dai dipendenti.</li>
                    <li><strong>Registro attività</strong>: chi ha fatto accesso, cosa ha scaricato e quando.</li>
                </ul>
            </>
        ),
    },
    {
        id: "documenti",
        icon: Archive,
        title: "Documenti e archivio",
        body: (
            <>
                <ul className="ml-4 list-disc space-y-1">
                    <li><strong>Archivio</strong>: cerca tra tutti i documenti con filtri per categoria, dipendente, presa visione e scadenza.</li>
                    <li><strong>Upload massivo</strong>: carica interi ZIP — i file vengono smistati automaticamente sui fascicoli giusti e i duplicati scartati.</li>
                    <li><strong>Da firmare</strong>: documenti in attesa di presa visione dei dipendenti, con solleciti.</li>
                    <li><strong>Da dipendenti</strong>: file inviati dai dipendenti tramite il portale.</li>
                    <li><strong>Documenti mancanti</strong> e <strong>Controllo buste paga</strong>: verifica cosa manca in ogni fascicolo, mese per mese.</li>
                    <li><strong>Documenti benvenuto</strong>: scegli i file che vengono assegnati automaticamente a ogni nuovo assunto.</li>
                </ul>
            </>
        ),
    },
    {
        id: "contratti",
        icon: FileText,
        title: "Contratti e UNILAV",
        body: (
            <>
                <ul className="ml-4 list-disc space-y-1">
                    <li>Dalla scheda dipendente, <strong>"Genera contratto"</strong> crea il DOCX dai modelli ufficiali con i dati CCNL già compilati.</li>
                    <li>Lo <strong>storico contratti</strong> tiene traccia di assunzioni, proroghe e trasformazioni.</li>
                    <li><strong>UNILAV massivo</strong>: trascina le ricevute PDF — l'AI le legge e le abbina al dipendente tramite codice fiscale.</li>
                    <li><strong>Modelli contratto</strong>: gestisci i template utilizzati dal generatore.</li>
                </ul>
            </>
        ),
    },
    {
        id: "acconti",
        icon: Wallet,
        title: "Acconti e prestiti",
        body: (
            <>
                <p>
                    Nella pagina <strong>Acconti</strong> gestisci acconti e prestiti: importi, piani di
                    trattenuta in busta paga e ricevute.
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li>Ogni ricevuta viene inviata al dipendente per la <strong>firma digitale</strong> sul portale.</li>
                    <li>Le <strong>richieste di acconto</strong> inviate dai dipendenti arrivano qui, pronte da approvare.</li>
                </ul>
            </>
        ),
    },
    {
        id: "tesserini",
        icon: IdCard,
        title: "Tesserini di riconoscimento",
        body: (
            <>
                <ul className="ml-4 list-disc space-y-1">
                    <li><strong>Upload Fototessere</strong>: carica le foto in blocco, il sistema le abbina ai dipendenti.</li>
                    <li>Anteprima <strong>fronte/retro</strong> del tesserino dalla scheda dipendente, con logo cliente opzionale.</li>
                    <li><strong>Stampa A4</strong> in due formati (85,6×54 mm o 10×7 cm), con retro specchiato per la stampa duplex.</li>
                    <li><strong>Storico stampe</strong> e <strong>Impostazioni</strong> (firma del datore, orientamento retro).</li>
                </ul>
            </>
        ),
    },
    {
        id: "permessi",
        icon: ShieldCheck,
        title: "Operatori e permessi",
        body: (
            <>
                <p>
                    Puoi dare accesso all'area amministrativa anche ad altri collaboratori, decidendo
                    esattamente cosa possono vedere:
                </p>
                <ol className="ml-4 list-decimal space-y-1">
                    <li>Apri la scheda del dipendente e attiva prima le <strong>credenziali portale</strong>, poi il <strong>"Ruolo amministratore"</strong>.</li>
                    <li>Nel pannello <strong>"Pagine operative abilitate"</strong> spunta le sole pagine consentite.</li>
                </ol>
                <p>
                    Le pagine non abilitate <strong>spariscono dal menu</strong> dell'operatore e sono
                    bloccate anche accedendo tramite link diretto.
                </p>
            </>
        ),
    },
    {
        id: "support",
        icon: Mail,
        title: "Assistenza",
        body: (
            <>
                <p>Per problemi tecnici o richieste sul portale:</p>
                <ul className="ml-4 list-none space-y-1">
                    <li className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-[#376DB3]" />
                        <a href="mailto:info@splendorsud.com" className="font-medium text-[#376DB3] hover:underline">
                            info@splendorsud.com
                        </a>
                    </li>
                    <li className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-[#376DB3]" />
                        <a href="tel:+39099375014" className="font-medium text-[#376DB3] hover:underline">
                            099.375014
                        </a>{" "}
                        <span className="text-slate-500">(ufficio)</span>
                    </li>
                </ul>
            </>
        ),
    },
];

const STORAGE_KEY_PREFIX = "ss_admin_guide_seen_";

export function hasSeenAdminGuide(userId) {
    if (!userId) return true;
    try {
        return localStorage.getItem(STORAGE_KEY_PREFIX + userId) === "1";
    } catch {
        return true;
    }
}

export function markAdminGuideSeen(userId) {
    if (!userId) return;
    try {
        localStorage.setItem(STORAGE_KEY_PREFIX + userId, "1");
    } catch {
        /* ignore */
    }
}

export function AdminGuideDialog({ open, onClose, userId, isFirstTime = false }) {
    const [closing, setClosing] = useState(false);

    const handleClose = () => {
        if (closing) return;
        setClosing(true);
        if (userId) markAdminGuideSeen(userId);
        onClose();
        setTimeout(() => setClosing(false), 300);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent
                className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0"
                data-testid="admin-guide-dialog"
            >
                <DialogHeader className="flex-shrink-0 bg-gradient-to-br from-[#15498F] to-[#376DB3] px-6 py-5 text-white">
                    <DialogTitle className="flex items-center gap-3 text-white">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20 backdrop-blur">
                            <BookOpen className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="font-display text-xl font-bold">
                                {isFirstTime ? "Benvenuto nell'area amministrativa" : "Guida all'area amministrativa"}
                            </div>
                            <div className="text-xs font-normal text-white/80">
                                Tutte le funzioni del portale di gestione HR
                            </div>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    {isFirstTime && (
                        <div className="mb-5 flex items-start gap-3 rounded-lg border border-[#376DB3]/20 bg-[#5BAEDB]/10 p-3">
                            <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#376DB3]" />
                            <p className="text-sm text-slate-700">
                                Questa guida riassume le aree del portale di gestione. Potrai sempre riaprirla
                                dal pulsante <strong>"Guida"</strong> in fondo al menu laterale.
                                Se sei un operatore, vedrai solo le pagine abilitate dal tuo amministratore.
                            </p>
                        </div>
                    )}

                    <div className="space-y-5">
                        {SECTIONS.map((sec, idx) => {
                            const Icon = sec.icon;
                            return (
                                <section key={sec.id} className="relative pl-12" data-testid={`admin-guide-section-${sec.id}`}>
                                    <div className="absolute left-0 top-0 flex h-9 w-9 items-center justify-center rounded-lg bg-[#376DB3] text-white shadow-sm">
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <h3 className="font-display text-base font-bold text-[#376DB3]">
                                        {idx + 1}. {sec.title}
                                    </h3>
                                    <div className="mt-1.5 space-y-2 text-sm leading-relaxed text-slate-700">
                                        {sec.body}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </div>

                <DialogFooter className="flex-shrink-0 gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:justify-between">
                    <div className="text-xs text-slate-500">
                        Trovi questa guida sempre disponibile dal pulsante "Guida" nel menu.
                    </div>
                    <Button
                        type="button"
                        onClick={handleClose}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="admin-guide-close-btn"
                    >
                        <CheckCircle2 className="h-4 w-4" />
                        {isFirstTime ? "Inizia" : "Chiudi"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
